import React, {useMemo} from 'react';
import {type FieldPath, type FieldValues, useController} from 'react-hook-form';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '@netx/core-components/Form';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {type IPerson} from '../../schema';
import {PersonAutoList, type PersonAutoListProps} from './PersonAutoList';

export interface ReactHookFormPersonAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
	PersonAutoListProps<Multiple, DisableClearable>,
	TFieldValues,
	TName
> {
	avatarFieldName?: string;
	valueLabelName?: string;
}

/**
 * `react-hook-form` wrapper for `PersonAutoList`
 */
export const ReactHookFormPersonAutoListPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		avatarFieldName,
		valueLabelName,
		ControllerProps,
		...props
	}: ReactHookFormPersonAutoListProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const fieldName = props.name || 'person-auto-list';

	avatarFieldName = avatarFieldName ?? `${fieldName}-avatarUrl`;
	valueLabelName = valueLabelName ?? `${fieldName}-displayLabel`;

	const valueLabelController = useController<TFieldValues, any>({
		control,
		name: valueLabelName,
		shouldUnregister: control._options.shouldUnregister,
		rules: ControllerProps?.rules,
	});

	const avatarController = useController<TFieldValues, any>({
		control,
		name: avatarFieldName,
		shouldUnregister: control._options.shouldUnregister,
		rules: ControllerProps?.rules,
	});

	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={fieldName as TName}
			render={({field, formState}) => {
				const value = useMemo(
					() =>
						field.value != null
							? Array.isArray(field.value)
								? (field.value as IPerson[]).map((v, idx) => {
										return {
											id: v,
											name: valueLabelController.field.value[idx],
											avatarUrl: avatarController.field.value[idx],
										};
									})
								: {
										id: field.value,
										name: valueLabelController.field.value,
										avatarUrl: avatarController.field.value,
									}
							: props.multiple
								? []
								: (null as any),
					[
						field.value,
						valueLabelController.field.value,
						avatarController.field.value,
					],
				);

				const handleChange = useConstCallback(
					splitEventHandler(props.onChange, (event, value) => {
						if (Array.isArray(value)) {
							const data = value.reduce<{
								ids: IPerson['id'][];
								names: IPerson['name'][];
								avatarUrls: IPerson['avatarUrl'][];
							}>(
								(accum, v) => {
									accum.ids.push(v.id);
									accum.names.push(v.name);
									accum.avatarUrls.push(v.avatarUrl);
									return accum;
								},
								{
									ids: [],
									names: [],
									avatarUrls: [],
								},
							);
							field.onChange(data.ids);
							valueLabelController.field.onChange(data.names);
							avatarController.field.onChange(data.avatarUrls);
							return;
						}
						field.onChange(value?.id || '');
						const label = value
							? value.name || i18n.t('i18n.unidentified')
							: '';
						valueLabelController.field.onChange(label);

						const avatar =
							!value || value?.id === 'new'
								? formState.defaultValues?.[avatarFieldName] || ''
								: value
									? value.avatarUrl || ''
									: '';

						avatarController.field.onChange(avatar);
					}),
				);

				return (
					<PersonAutoList<Multiple, DisableClearable>
						isNew={field.value === 'new'}
						avatarUrl={avatarController.field.value}
						error={formState.errors[fieldName]?.message?.toString()}
						{...props}
						{...field}
						disabled={props.disabled || field.disabled}
						ref={combineRefs(ref, field.ref)}
						value={value}
						onChange={handleChange}
					/>
				);
			}}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` wrapper for `PersonAutoList`
 */
export const ReactHookFormPersonAutoList = React.forwardRef(
	ReactHookFormPersonAutoListPrimitive,
) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormPersonAutoListProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
) => JSX.Element;

export default ReactHookFormPersonAutoList;
